/**
 * Seasons, ranked tiers, the battle pass and coins. Pure rules shared by the
 * server (which is the only one that ever awards anything) and the client
 * (which only displays them).
 *
 * A season is one UTC calendar month ("2026-10"). Ranked matches move season
 * rank points (RP); RP decide the tier. Every online match also earns coins and
 * battle-pass XP for the current season.
 */

export type TierId = 'bronze' | 'silver' | 'gold' | 'diamond' | 'crown' | 'ace' | 'conqueror';

export interface TierDef {
  id: TierId;
  /** Rank points needed to reach this tier. Conqueror also needs a top CONQUEROR_TOP season rank. */
  min: number;
  icon: string;
  color: string;
  /** Tiers below Ace are split into III, II, I. */
  divisions: boolean;
  /** RP multipliers: low tiers climb fast and barely drop, top tiers are hard to hold. */
  gain: number;
  loss: number;
}

export const TIERS: TierDef[] = [
  { id: 'bronze', min: 0, icon: '🥉', color: '#cd7f32', divisions: true, gain: 1.6, loss: 0.3 },
  { id: 'silver', min: 500, icon: '🥈', color: '#c0c7d1', divisions: true, gain: 1.4, loss: 0.6 },
  { id: 'gold', min: 1000, icon: '🥇', color: '#ffd166', divisions: true, gain: 1.2, loss: 0.8 },
  { id: 'diamond', min: 1600, icon: '💎', color: '#5ee7ff', divisions: true, gain: 1, loss: 1 },
  { id: 'crown', min: 2300, icon: '👑', color: '#ff9f43', divisions: true, gain: 0.9, loss: 1.15 },
  { id: 'ace', min: 3100, icon: '🎖️', color: '#ff4d6d', divisions: false, gain: 0.8, loss: 1.3 },
  { id: 'conqueror', min: 3100, icon: '🏆', color: '#b388ff', divisions: false, gain: 0.8, loss: 1.3 },
];

/** The top this many Ace players of a season (by RP) are shown as Conqueror. */
export const CONQUEROR_TOP = 100;

export const tierDef = (id: TierId): TierDef => TIERS.find((t) => t.id === id)!;

export interface TierInfo {
  tier: TierId;
  /** 3, 2 or 1 (III is the lowest); null for Ace and Conqueror. */
  division: 1 | 2 | 3 | null;
  /** Progress toward the next division/tier, 0..1 (1 at the top). */
  progress: number;
  /** RP needed for the next division/tier, or null at the top. */
  next: number | null;
}

/** Tier from season RP; `rank` is the player's season rank (for Conqueror), if known. */
export function tierFor(points: number, rank: number | null = null): TierInfo {
  const climbable = TIERS.filter((t) => t.id !== 'conqueror');
  let i = climbable.length - 1;
  while (i > 0 && points < climbable[i]!.min) i--;
  const t = climbable[i]!;
  if (t.id === 'ace') {
    const conqueror = rank !== null && rank <= CONQUEROR_TOP;
    return { tier: conqueror ? 'conqueror' : 'ace', division: null, progress: 1, next: null };
  }
  const nextMin = climbable[i + 1]!.min;
  const band = (nextMin - t.min) / 3;
  const step = Math.min(2, Math.floor((points - t.min) / band));
  const lo = t.min + step * band;
  return {
    tier: t.id,
    division: (3 - step) as 1 | 2 | 3,
    progress: Math.max(0, Math.min(1, (points - lo) / band)),
    next: Math.round(lo + band),
  };
}

export const tierIndex = (id: TierId) => TIERS.findIndex((t) => t.id === id);

/** "2026-10" for a date (UTC). */
export function seasonOf(d: Date = new Date()): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function seasonStart(season: string): Date {
  const [y, m] = season.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1));
}

export function seasonEnd(season: string): Date {
  const [y, m] = season.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 1));
}

export function previousSeason(season: string): string {
  const start = seasonStart(season);
  return seasonOf(new Date(start.getTime() - 86_400_000));
}

/** Starting RP in a new season: half of last season's, never above the Diamond floor. */
export function softReset(previousPoints: number): number {
  return Math.min(tierDef('diamond').min, Math.floor(previousPoints / 2));
}

export interface RankedOutcome {
  /** 1 = best. In team matches: 1 for the winning team, 2 otherwise (both 1 on a draw). */
  place: number;
  /** Number of places in play (players in FFA, 2 for teams). */
  of: number;
  kills: number;
}

/**
 * RP for one ranked match: +30 for first place down to -20 for last, plus 4 per
 * kill (up to 5), then scaled by the tier's gain/loss multipliers.
 */
export function rankPointsDelta(o: RankedOutcome, current: number): number {
  const frac = o.of > 1 ? (o.of - o.place) / (o.of - 1) : 1;
  const raw = -20 + 50 * frac + Math.min(5, o.kills) * 4;
  const t = tierDef(tierFor(current).tier);
  const delta = Math.round(raw >= 0 ? raw * t.gain : raw * t.loss);
  return Math.max(-current, delta);
}

/** End-of-season rewards by the best tier reached that season. */
export const SEASON_REWARDS: Record<TierId, { coins: number; skin?: string }> = {
  bronze: { coins: 50 },
  silver: { coins: 120 },
  gold: { coins: 250 },
  diamond: { coins: 450 },
  crown: { coins: 700, skin: 'crown-gold' },
  ace: { coins: 1000, skin: 'ace-blaze' },
  conqueror: { coins: 1500, skin: 'conqueror' },
};

// ----- coins & battle-pass XP -----

export interface MatchRewardInput {
  won: boolean;
  /** 0 (last) .. 1 (first). */
  placeFrac: number;
  kills: number;
  /** Matches with fewer than two humans (you vs bots) pay half. */
  vsHumans: boolean;
}

export function coinsForMatch(m: MatchRewardInput): number {
  const base = 5 + (m.won ? 15 : Math.round(m.placeFrac * 8)) + Math.min(5, m.kills) * 2;
  return m.vsHumans ? base : Math.ceil(base / 2);
}

export function xpForMatch(m: MatchRewardInput): number {
  const base = 40 + (m.won ? 40 : Math.round(m.placeFrac * 20)) + Math.min(5, m.kills) * 10;
  return m.vsHumans ? base : Math.ceil(base / 2);
}

/** Coins from matches stop after this many per UTC day (anti-farming). */
export const DAILY_MATCH_COIN_CAP = 250;
/** Completing all of a day's missions. */
export const MISSION_DAY_COINS = 60;
export const MISSION_DAY_XP = 150;

// ----- battle pass -----

export const PASS_LEVELS = 30;
export const PASS_XP_PER_LEVEL = 250;
/** Premium track without a subscription (Premium subscribers get it included). */
export const PASS_PRICE_COINS = 1200;

export type PassReward = { kind: 'coins'; amount: number } | { kind: 'skin' | 'theme'; id: string };

export interface PassLevel {
  level: number;
  free: PassReward | null;
  premium: PassReward;
}

const coins = (amount: number): PassReward => ({ kind: 'coins', amount });

/** The same 30-level ladder every season; pass cosmetics can only be earned here. */
export const PASS_TRACK: PassLevel[] = Array.from({ length: PASS_LEVELS }, (_, i) => {
  const level = i + 1;
  const special: Record<number, { free?: PassReward | null; premium?: PassReward }> = {
    5: { premium: { kind: 'skin', id: 'tiger' } },
    10: { free: { kind: 'skin', id: 'galaxy' } },
    15: { premium: { kind: 'theme', id: 'neon-grid' } },
    20: { free: { kind: 'theme', id: 'sunrise' }, premium: { kind: 'skin', id: 'hydra' } },
    25: { premium: coins(400) },
    30: { free: coins(300), premium: { kind: 'skin', id: 'season-viper' } },
  };
  const s = special[level] ?? {};
  return {
    level,
    free: s.free !== undefined ? s.free : level % 2 === 0 ? coins(level % 10 === 0 ? 150 : 40) : null,
    premium: s.premium ?? coins(level % 5 === 0 ? 200 : 60),
  };
});

export const passLevelFor = (xp: number) => Math.min(PASS_LEVELS, Math.floor(xp / PASS_XP_PER_LEVEL));
